import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { Alert, Button } from 'react-bootstrap';
import axios from 'axios';
import { ImpersonationState, ImpersonationStore, StudentAccess } from '../services/ImpersonationStore';
import { GraduationApi, errorMessage } from '../services/GraduationApi';
import AuthService from '../services/AuthService';
import { GraduateStaffModeStore } from '../services/GraduateStaffModeStore';
import { graduateStaffAccess, graduateStaffKind } from '../services/GraduateStaffAccess';
import ImpersonationEmailControls from './ImpersonationEmailControls';

export function useAuthSession() {
  const snapshot = useSyncExternalStore(AuthService.subscribe ?? (() => () => {}),
    AuthService.sessionSnapshot ?? (() => JSON.stringify(AuthService.getCurrentToken())));
  return snapshot ? JSON.parse(snapshot) as ReturnType<typeof AuthService.getCurrentToken> : null;
}

export function useImpersonation() {
  return useSyncExternalStore(ImpersonationStore.subscribe, ImpersonationStore.get);
}
export function useStudentAccess(): StudentAccess | null {
  const impersonation = useImpersonation();
  const account = AuthService.getCurrentToken();
  if (!account) return null;
  return impersonation ? (impersonation.session.kind || 'Student') === 'Student' ? impersonation.session.access || null : null
    : account.studentAccess || null;
}
export function useCanViewUndergraduate() {
  return Boolean(useStudentAccess()?.undergraduateAccess);
}
export function useCanViewGraduate() {
  const access = useStudentAccess();
  return Boolean(access && (access.graduateAccess || (access.undergraduateAccess && access.certAccess)));
}
export function useCanEvaluateUndergraduate() {
  const impersonation = useImpersonation();
  const account = AuthService.getCurrentToken();
  return Boolean(account && (impersonation ? impersonation.session.kind === 'Evaluator' :
    (account.isUndergraduateAdministrator ?? account.canImpersonate) || account.canEvaluateUndergraduate));
}
export function useCanViewUndergraduateApplications() {
  const impersonation = useImpersonation();
  const account = AuthService.getCurrentToken();
  return Boolean(account && (impersonation ? impersonation.session.kind === 'Evaluator' || impersonation.session.kind === 'Viewer' :
    (account.isUndergraduateAdministrator ?? account.canImpersonate) || account.canEvaluateUndergraduate || account.canViewUndergraduateApplications));
}
export function useCanViewGraduateStaff() {
  const impersonation = useImpersonation();
  const account = useAuthSession();
  const { canEvaluate, canReview } = graduateStaffAccess(account, impersonation);
  return canEvaluate || canReview;
}
export function useGraduateStaffMode() {
  const impersonation = useImpersonation();
  const account = useAuthSession();
  const selected = useSyncExternalStore(GraduateStaffModeStore.subscribe, GraduateStaffModeStore.get);
  const kind = graduateStaffKind(account, impersonation, selected);
  return { kind, canEdit: !impersonation || impersonation.session.mode === 'Edit',
    ...graduateStaffAccess(account, impersonation) } as const;
}
export function useCanEditGraduateApplication() {
  const account = useAuthSession();
  const impersonation = useImpersonation();
  const isAdmin = Array.isArray(account?.roles)
    ? account!.roles!.some(role => ['accessadministrator', 'graduateadministrator'].includes(role.toLowerCase()))
    : Boolean(account?.isGraduateAdministrator);
  return isAdmin && !impersonation;
}
export function useCanManageGraduateAdminNotes() {
  return useCanEditGraduateApplication();
}
export default function StudentWorkspace({ children }: { children: React.ReactNode }) {
  const state = useImpersonation();
  const mode = state?.session.mode === 'Edit' ? 'Edit' : 'View';
  const [verified, setVerified] = useState<ImpersonationState | null>(null);
  const [error, setError] = useState('');
  const [stopping, setStopping] = useState(false);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') AuthService.refreshCurrentSession().catch(() => {}); };
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); window.clearInterval(timer); };
  }, []);
  useEffect(() => {
    setError('');
    if (!state) { setVerified(null); return; }
    if (ImpersonationStore.isInvalidated(state)) {
      setVerified(null); setError('The impersonation session is no longer active. Stop impersonation and select the user again.'); return;
    }
    const delay = Date.parse(state.session.expiresOnUtc) - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) {
      setVerified(null); setError('This impersonation session has expired. Stop impersonation and select the user again.'); return;
    }
    const controller = new AbortController();
    if (!ImpersonationStore.isStarted(state) && verified !== state) GraduationApi.verify(controller.signal).then(result => {
      if (controller.signal.aborted) return;
      if (result.impersonation?.sessionId !== state.session.sessionId || (result.impersonation.mode || 'View') !== mode ||
        (result.impersonation.kind || 'Student') !== (state.session.kind || 'Student') ||
        result.impersonation.student.starId !== state.session.student.starId) throw new Error('The impersonation session is no longer active. Stop impersonation to continue.');
      const next = JSON.stringify(result.impersonation.access) !== JSON.stringify(state.session.access)
        ? { ...state, session: { ...state.session, access: result.impersonation.access } } : state;
      if (next !== state) ImpersonationStore.set(next);
      setVerified(next);
    }).catch(e => { if (!controller.signal.aborted && !axios.isCancel(e)) { setVerified(null); setError(errorMessage(e)); } });
    const timer = window.setTimeout(() => { setVerified(null); setError('This impersonation session has expired. Stop impersonation and select the user again.'); }, Math.min(delay, 2147483647));
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [state, mode, verified]);
  const stop = async () => {
    setStopping(true);
    try { await GraduationApi.stop(); ImpersonationStore.set(null); }
    catch (e) { setError(errorMessage(e)); }
    finally { setStopping(false); }
  };
  return <>
    {state && <Alert variant="warning" aria-label={mode === 'Edit' ? 'Edit impersonation' : 'Read-only impersonation'} className="d-flex flex-wrap justify-content-between align-items-center gap-3">
      <div><strong>Viewing as {state.session.student.displayName} — {mode === 'Edit' ? 'Edit mode' : 'read-only'}</strong>
        <div>{state.session.kind === 'Evaluator' ? 'Undergraduate evaluator' : state.session.kind === 'Viewer' ? 'Undergraduate viewer' :
          state.session.kind === 'GraduateEvaluator' ? 'Graduate evaluator' : state.session.kind === 'GraduateFaculty' ? 'Graduate faculty' :
          'Student'} · Tech ID: {state.session.student.techId} · StarID: {state.session.student.starId}</div>
        <small>Expires {new Date(state.session.expiresOnUtc).toLocaleTimeString()}. You remain signed in as the administrator. {mode === 'Edit' &&
          (state.session.kind === 'Evaluator' || state.session.kind === 'GraduateEvaluator' || state.session.kind === 'GraduateFaculty' ?
            'Staff changes are audited with your administrator identity.' : 'Changes are saved for this student and attributed to you.')}</small></div>
      <Button variant="outline-dark" disabled={stopping} onClick={stop}>{stopping ? 'Stopping…' : 'Stop impersonation'}</Button>
    </Alert>}
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {state && (ImpersonationStore.isInvalidated(state) || Date.parse(state.session.expiresOnUtc) <= Date.now() ||
      (!ImpersonationStore.isStarted(state) && verified !== state)) ? (!error && <p role="status">Verifying the impersonation session…</p>) :
      <React.Fragment key={state ? `${state.session.sessionId}:${mode}` : 'self'}>{state && <ImpersonationEmailControls sessionId={state.session.sessionId} />}{children}</React.Fragment>}
  </>;
}


